import { type useForm } from '@tanstack/react-form';
import { UserPlus01 } from '@untitled-ui/icons-react';

import { Button } from '../../../../../../components/buttons/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../../../../../../components/dialog';
import { FeaturedIcon } from '../../../../../../components/featured-icon';
import { TextField } from '../../../../../../components/inputs/text-field';
import { userCreateSchema } from '../../../../../../entity/api/user';
import { tGlobal, tNamespace } from '../../../../../../services/translation';
import { parseFirstZodError } from '../../../../../../shared/validation';
import { type UserInviteForm } from '../_tabs-layout/users';

const t = tNamespace('app.settings.organization.users');

type UserInviteUserDialogProps = {
  dialogInviteState: boolean;
  setDialogInviteState: (state: boolean) => void;
  form: ReturnType<typeof useForm<UserInviteForm>>;
  handleInviteUser: () => void;
  isLoading: boolean;
};

export function UserInviteUserDialog(props: UserInviteUserDialogProps) {
  const { dialogInviteState, setDialogInviteState, form, handleInviteUser, isLoading } = props;

  return (
    <Dialog open={dialogInviteState} onOpenChange={setDialogInviteState}>
      <DialogContent hasCloseButton>
        <div className="mb-5 flex w-full">
          <FeaturedIcon icon={<UserPlus01 />} size="md" color="accent" border={false} />
        </div>
        <DialogTitle>{t('addUserToOrganization')}</DialogTitle>
        <DialogDescription className="mb-5">{t('inviteInformation')}</DialogDescription>
        <form>
          <form.Field
            name="email"
            validators={{
              onSubmit: function (data) {
                return parseFirstZodError(userCreateSchema.shape.email, data.value);
              },
              onChange: function (data) {
                if (!data.fieldApi.form.state.isSubmitted) {
                  return undefined;
                }
                return parseFirstZodError(userCreateSchema.shape.email, data.value);
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  value={field.state.value}
                  placeholder={t('newUserEmail')}
                  sizeField="lg"
                  error={field.state.meta?.errors?.[0]}
                  onChange={(e) => {
                    field.handleChange(e.target.value);
                    if (form.state.isSubmitted) {
                      field.setErrorMap({ onSubmit: undefined });
                    }
                  }}
                />
              );
            }}
          />
        </form>
        <DialogFooter isColumn>
          <Button
            variant="outline"
            size="xl"
            className="w-full"
            onClick={function () {
              setDialogInviteState(false);
            }}
          >
            {tGlobal('cancel')}
          </Button>
          <Button
            loading={isLoading}
            size="xl"
            variant="accent"
            className="w-full"
            onClick={function () {
              void handleInviteUser();
            }}
          >
            {t('sendInvite')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
